type BrazeSdk = typeof import('@braze/web-sdk');

const BRAZE_STORAGE_PREFIX = 'ab.storage.';
const BRAZE_INDEXED_DB_NAME = 'AppboyServiceWorkerAsyncStorage';

// The SDK is a single shared instance, so whether it's running is tracked
// here rather than in a hook
let brazeSdk: BrazeSdk | undefined;
let loadRequested = false;
// If consent is withdrawn and given again while the SDK downloads, two loads
// can be waiting on it at once, and only the latest should start Braze
let latestLoad = 0;
// Who to set as the Braze user once the SDK has loaded. Logging in or out
// while it's still downloading updates this.
let pendingBrazeExternalId: string | undefined;

// Braze's own wipeData only works while the SDK is running, and we don't want
// to start Braze for someone without marketing consent just to clean up after
// an earlier visit. So this removes what Braze stores directly.
export const removeStoredBrazeData = () => {
  let foundBrazeData = false;

  try {
    Object.keys(localStorage)
      .filter((key) => key.startsWith(BRAZE_STORAGE_PREFIX))
      .forEach((key) => {
        foundBrazeData = true;
        localStorage.removeItem(key);
      });
  } catch {
    // localStorage can throw when the browser blocks it
  }

  const brazeCookieNames = document.cookie
    .split(';')
    .map((cookie) => cookie.split('=')[0].trim())
    .filter((name) => name.startsWith(BRAZE_STORAGE_PREFIX));

  // Braze sets its cookies on the widest domain the browser allows (e.g.
  // .sofarsounds.com), and a cookie can only be removed with the same domain,
  // so try each parent domain of this host
  const hostParts = window.location.hostname.split('.');
  const domains = hostParts.map((_, index) => hostParts.slice(index).join('.'));
  const expired = `expires=${new Date(0).toUTCString()};path=/`;
  brazeCookieNames.forEach((name) => {
    foundBrazeData = true;
    document.cookie = `${name}=;${expired}`;
    domains.forEach((domain) => {
      document.cookie = `${name}=;${expired};domain=.${domain}`;
    });
  });

  if (foundBrazeData) {
    window.indexedDB?.deleteDatabase(BRAZE_INDEXED_DB_NAME);
  }
};

// Only call this with marketing consent. The SDK is loaded here rather than
// imported at the top so that only visitors on pages that use Braze download
// it.
export const loadBraze = async (brazeExternalId?: string) => {
  const appIdentifier = process.env.BRAZE_APP_IDENTIFIER;
  const sdkEndpoint = process.env.BRAZE_SDK_ENDPOINT;

  if (loadRequested || !appIdentifier || !sdkEndpoint) {
    return;
  }
  loadRequested = true;
  pendingBrazeExternalId = brazeExternalId;
  latestLoad += 1;
  const thisLoad = latestLoad;

  let sdk: BrazeSdk;
  try {
    sdk = await import('@braze/web-sdk');
  } catch {
    if (thisLoad === latestLoad) {
      loadRequested = false;
    }
    return;
  }

  // Consent may have been withdrawn while the SDK was downloading
  if (thisLoad !== latestLoad) {
    return;
  }

  const initialized = sdk.initialize(appIdentifier, {
    baseUrl: sdkEndpoint,
    enableLogging: process.env.BRAZE_ENABLE_LOGGING === 'true',
  });

  if (!initialized) {
    loadRequested = false;
    return;
  }

  // Set the user before opening the session, otherwise Braze can record two
  // session starts for a logged-in user
  if (pendingBrazeExternalId) {
    sdk.changeUser(pendingBrazeExternalId);
  }
  sdk.openSession();
  brazeSdk = sdk;
};

// Braze ignores changeUser with the current ID
export const setBrazeUser = (brazeExternalId: string) => {
  if (brazeSdk) {
    brazeSdk.changeUser(brazeExternalId);
  } else {
    pendingBrazeExternalId = brazeExternalId;
  }
};

// For when marketing consent is withdrawn or missing
export const stopBraze = () => {
  loadRequested = false;
  latestLoad += 1;
  pendingBrazeExternalId = undefined;

  if (brazeSdk) {
    brazeSdk.wipeData();
    brazeSdk.destroy();
    brazeSdk = undefined;
  } else {
    removeStoredBrazeData();
  }
};

// For logout. Braze has no way to undo changeUser, so without this Braze keeps
// treating the browser as the last user's, and the next person to use it could
// see in-app messages meant for them. Wiping makes Braze treat the browser as a
// new anonymous user.
export const resetBrazeUser = () => {
  pendingBrazeExternalId = undefined;

  if (brazeSdk) {
    brazeSdk.wipeData();
  } else {
    removeStoredBrazeData();
  }
};
